import { getMyNotifications, markNotificationsRead } from "api/notifications";
import type { NotificationDto } from "api/types";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Paths, { getTreeViewPath } from "routes/Paths";
import { useSession } from "Session";
import { formatLastModified, notificationCopy } from "utilities";

import ViewIcon from "./ViewIcon";

import styles from "./NotificationBell.module.css";

const getInitials = (name: string): string =>
	name
		.split(/\s+/)
		.filter(Boolean)
		.slice(0, 2)
		.map((word: string) => word[0]?.toUpperCase() ?? "")
		.join("") || "U";

/**
 * Header bell with an unread badge and a dropdown of recent notifications.
 * Unread rows stay highlighted until "Mark all as read" is clicked; the
 * footer links to the full notifications page.
 */
const NotificationBell = () => {
	const { t } = useTranslation("homePage");
	const navigate = useNavigate();
	const { getAccessToken } = useSession();
	const [notifications, setNotifications] = useState<NotificationDto[]>([]);
	const [unreadCount, setUnreadCount] = useState(0);
	const [isOpen, setIsOpen] = useState(false);
	const wrapperRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		let isCurrent = true;

		const load = async (): Promise<void> => {
			const token = await getAccessToken();
			if (!token || !isCurrent) {
				return;
			}

			const loaded = await getMyNotifications(token);
			if (!isCurrent) {
				return;
			}

			setNotifications(loaded);
			setUnreadCount(loaded.filter((notification: NotificationDto) => !notification.isRead).length);
		};

		load().catch(() => undefined);

		return () => {
			isCurrent = false;
		};
	}, [getAccessToken]);

	// Close when clicking anywhere outside the bell or its dropdown.
	useEffect(() => {
		if (!isOpen) {
			return undefined;
		}

		const onDocumentClick = (event: MouseEvent): void => {
			if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
				setIsOpen(false);
			}
		};

		document.addEventListener("mousedown", onDocumentClick);

		return () => document.removeEventListener("mousedown", onDocumentClick);
	}, [isOpen]);

	const toggleOpen = useCallback((): void => setIsOpen((open: boolean) => !open), []);

	const onMarkAllRead = useCallback((): void => {
		setUnreadCount(0);
		setNotifications((current: NotificationDto[]) =>
			current.map((notification: NotificationDto) => ({ ...notification, isRead: true }))
		);
		getAccessToken()
			.then((token: string) => (token ? markNotificationsRead(token) : undefined))
			.catch(() => undefined);
	}, [getAccessToken]);

	const onViewAll = useCallback((): void => {
		setIsOpen(false);
		navigate(Paths.notifications);
	}, [navigate]);

	const onNotificationClick = useCallback(
		(event: React.MouseEvent<HTMLButtonElement>): void => {
			const treeId = event.currentTarget.dataset.treeId;
			if (!treeId) {
				return;
			}

			setIsOpen(false);
			// A shared tree is not viewable until accepted, so shares land on "Shared with Me".
			if (event.currentTarget.dataset.notificationType === "TreeShared") {
				navigate(Paths.sharedWithMe);

				return;
			}

			navigate(getTreeViewPath(treeId));
		},
		[navigate]
	);

	const messageFor = (notification: NotificationDto): string => {
		const copy = notificationCopy(notification);

		return t(copy.key, copy.values);
	};

	return (
		<div ref={wrapperRef} className={styles.wrapper}>
			<button
				aria-expanded={isOpen}
				aria-label={t("view.notifications")}
				className={styles.bellButton}
				onClick={toggleOpen}
				type="button">
				<ViewIcon name="bell" />
				{unreadCount > 0 && <span className={styles.badge}>{unreadCount > 9 ? "9+" : unreadCount}</span>}
			</button>

			{isOpen && (
				<div className={styles.dropdown} role="menu">
					<div className={styles.dropdownHeader}>
						<h3 className={styles.dropdownTitle}>{t("view.notifications")}</h3>
						{unreadCount > 0 && (
							<button className={styles.markAllButton} onClick={onMarkAllRead} type="button">
								{t("view.markAllRead")}
							</button>
						)}
					</div>
					{notifications.length === 0 ? (
						<p className={styles.emptyState}>{t("view.noNotifications")}</p>
					) : (
						<div className={styles.list}>
							{notifications.map((notification: NotificationDto) => (
								<button
									key={notification.id}
									className={notification.isRead ? styles.item : styles.unreadItem}
									data-notification-type={notification.type}
									data-tree-id={notification.treeId}
									onClick={onNotificationClick}
									role="menuitem"
									type="button">
									<span className={styles.actorAvatar}>
										{notification.actorImageUrl ? (
											<img alt="" referrerPolicy="no-referrer" src={notification.actorImageUrl} />
										) : (
											getInitials(notification.actorName)
										)}
									</span>
									<span className={styles.itemBody}>
										<span className={styles.itemMessage}>{messageFor(notification)}</span>
										<span className={styles.itemTime}>
											{formatLastModified(notification.createdDate)}
										</span>
									</span>
								</button>
							))}
						</div>
					)}
					<div className={styles.dropdownFooter}>
						<button className={styles.viewAllButton} onClick={onViewAll} type="button">
							{t("view.viewAllNotifications")}
						</button>
					</div>
				</div>
			)}
		</div>
	);
};

export default NotificationBell;
